import * as React from "react";
import { createPortal } from "react-dom";
import { CircleAlert, CircleCheck, Info, X } from "lucide-react";
import { IconTile } from "./IconTile";
import { ToastContext, type ToastApi, type ToastOptions, type ToastTone } from "./toast-context";

/**
 * The toaster behind `useToast`. The context and the hook live in
 * `toast-context.ts`, and the split is deliberate - see the header there.
 *
 * **This file must export components and nothing else.**
 *
 * ── The top layer, which is the whole reason this is not a plain portal ─────
 *
 * `Modal` and `Drawer` are native `<dialog>`s opened with `showModal()`, which
 * promotes them to the browser's **top layer**. Nothing in the normal stacking
 * context reaches it - `z-index: 9999` on a portalled div still renders *under*
 * an open dialog and its backdrop. So a toast raised while a dialog is open
 * (resending an invitation, the one action here that deliberately leaves its
 * dialog on screen) would be invisible.
 *
 * The toaster is therefore a `popover="manual"` element, which is the other way
 * into the top layer and the only one that does not also trap focus, render a
 * backdrop and make the page inert. `manual` rather than `auto`: an `auto`
 * popover light-dismisses on any outside click, so the first click anywhere on
 * the page would take the confirmation with it.
 *
 * Top-layer order is promotion order, so a dialog opened *after* the toaster
 * was shown sits above it. `promote()` below re-shows the toaster when that is
 * actually the case, rather than on every toast - hiding and re-showing a
 * popover restarts its descendants' CSS animations, which would replay the
 * entrance on toasts that have been sitting there for three seconds.
 */

interface ToastItem {
  id: number;
  tone: ToastTone;
  message: string;
  description?: string;
  /** On its way out. Kept mounted just long enough for the exit to run. */
  leaving?: boolean;
}

/** Long enough to read two lines, short enough not to sit over the page. */
const DURATION = 4500;
/** Must match `.toast--leaving`'s animation in `ui.css`. */
const EXIT_MS = 160;

/**
 * Older toasts are dropped rather than stacked past this.
 *
 * A column of eight covers the content that the eighth one is describing, and
 * nobody reads the fourth: whatever produced eight in a row is a loop, and the
 * useful thing on screen at that point is the list underneath.
 */
const MAX_VISIBLE = 3;

const TONE_ICON: Record<ToastTone, React.ReactNode> = {
  success: <CircleCheck />,
  error: <CircleAlert />,
  info: <Info />,
};

/** `ToastTone` names match `.toast--{tone}`; `IconTile`'s tones are named for
 *  the token they paint with, so "success" and "error" become "ok" and "stop"
 *  here. */
const TONE_TILE: Record<ToastTone, "ok" | "stop" | "advisory"> = {
  success: "ok",
  error: "stop",
  info: "advisory",
};

/** A toast's dismissal clock, kept open so hovering can hold it mid-flight. */
interface Clock {
  timer: ReturnType<typeof setTimeout> | null;
  /** Milliseconds still owed when the clock was last started. */
  remaining: number;
  startedAt: number;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = React.useState<ToastItem[]>([]);
  const [mounted, setMounted] = React.useState(false);
  const toasterRef = React.useRef<HTMLDivElement>(null);
  const nextId = React.useRef(0);

  /**
   * The timers, in a ref rather than in state.
   *
   * They are imperative and a re-render must not restart a countdown - which is
   * exactly what putting them in state would do, since every new toast
   * re-renders the whole toaster.
   */
  const clocks = React.useRef(new Map<number, Clock>());
  const held = React.useRef(false);
  /** Exit timers, tracked so an unmount mid-animation leaves nothing firing. */
  const exits = React.useRef(new Set<ReturnType<typeof setTimeout>>());

  React.useEffect(() => setMounted(true), []);

  const dismiss = React.useCallback((id: number) => {
    const clock = clocks.current.get(id);
    if (clock?.timer) clearTimeout(clock.timer);
    clocks.current.delete(id);

    // Flagged first, dropped after. A toast that simply disappeared would take
    // the stack below it with it in the same frame, which reads as a glitch
    // rather than as a dismissal.
    setItems((current) => current.map((it) => (it.id === id ? { ...it, leaving: true } : it)));

    const timer = setTimeout(() => {
      exits.current.delete(timer);
      setItems((current) => current.filter((it) => it.id !== id));
    }, EXIT_MS);
    exits.current.add(timer);
  }, []);

  /**
   * Put the toaster back on top of the top layer, but only when something has
   * since been stacked above it. See the header - re-showing unconditionally
   * replays every visible toast's entrance animation.
   *
   * Both calls are guarded on the current visibility state rather than wrapped
   * in a try: `showPopover()` on a shown popover and `hidePopover()` on a hidden
   * one both throw `InvalidStateError`, and the hidden case happens on the very
   * first layout effect, before a toast has ever been raised.
   */
  const promote = React.useCallback(() => {
    const el = toasterRef.current;
    if (!el) return;

    const shown = el.matches(":popover-open");
    if (shown && !document.querySelector("dialog[open]")) return;
    if (shown) el.hidePopover();
    el.showPopover();
  }, []);

  const push = React.useCallback(
    (tone: ToastTone, message: string, options?: ToastOptions) => {
      const id = (nextId.current += 1);

      setItems((current) => [...current, { id, tone, message, description: options?.description }]);

      clocks.current.set(id, {
        remaining: DURATION,
        startedAt: Date.now(),
        // A toast raised while the pointer is already resting on the toaster
        // starts held, so it cannot slip away before it has been read.
        timer: held.current ? null : setTimeout(() => dismiss(id), DURATION),
      });
    },
    [dismiss]
  );

  // Promotion has to happen after the popover has a rendered box, and dropping
  // the overflow has to happen after the new item is in state - both are
  // reactions to `items`, not to the click that produced it.
  React.useLayoutEffect(() => {
    const el = toasterRef.current;
    if (items.length === 0) {
      if (el?.matches(":popover-open")) el.hidePopover();
      return;
    }
    promote();

    const excess = items.filter((it) => !it.leaving).length - MAX_VISIBLE;
    if (excess > 0) {
      for (const it of items.filter((i) => !i.leaving).slice(0, excess)) dismiss(it.id);
    }
  }, [items, promote, dismiss]);

  const hold = React.useCallback(() => {
    if (held.current) return;
    held.current = true;
    const now = Date.now();
    for (const clock of clocks.current.values()) {
      if (!clock.timer) continue;
      clearTimeout(clock.timer);
      clock.timer = null;
      clock.remaining = Math.max(0, clock.remaining - (now - clock.startedAt));
    }
  }, []);

  const release = React.useCallback(() => {
    if (!held.current) return;
    held.current = false;
    const now = Date.now();
    for (const [id, clock] of clocks.current) {
      clock.startedAt = now;
      clock.timer = setTimeout(() => dismiss(id), clock.remaining);
    }
  }, [dismiss]);

  // Timers outlive React's teardown, so they are cancelled by hand.
  React.useEffect(() => {
    const pending = clocks.current;
    const leaving = exits.current;
    return () => {
      for (const clock of pending.values()) if (clock.timer) clearTimeout(clock.timer);
      pending.clear();
      for (const timer of leaving) clearTimeout(timer);
      leaving.clear();
    };
  }, []);

  const api = React.useMemo<ToastApi>(
    () => ({
      success: (message, options) => push("success", message, options),
      error: (message, options) => push("error", message, options),
      info: (message, options) => push("info", message, options),
    }),
    [push]
  );

  return (
    <ToastContext.Provider value={api}>
      {children}

      {mounted &&
        createPortal(
          <div
            ref={toasterRef}
            popover="manual"
            className="toaster"
            onMouseEnter={hold}
            onMouseLeave={release}
            // Keyboard users reach the dismiss button by tabbing, and the toast
            // must not expire out from under the focus ring while they do.
            onFocusCapture={hold}
            onBlurCapture={release}
          >
            {items.map((item) => (
              <div
                key={item.id}
                className={`toast toast--${item.tone}${item.leaving ? " toast--leaving" : ""}`}
                // `status` rather than `alert`: a success is polite, and an
                // assertive live region interrupts whatever a screen reader is
                // in the middle of saying.
                role="status"
              >
                {/* Icon and title share one row, centred on each other - the
                    title is always one line, so there is no "top of a
                    paragraph" for the icon to align with instead. The
                    description, when there is one, sits below the row rather
                    than inside it, indented under the title rather than the
                    icon. */}
                <div className="toast__row">
                  <IconTile tone={TONE_TILE[item.tone]} size="sm">
                    {TONE_ICON[item.tone]}
                  </IconTile>

                  <p className="toast__message">{item.message}</p>

                  <button
                    type="button"
                    className="toast__close"
                    aria-label="Dismiss"
                    onClick={() => dismiss(item.id)}
                  >
                    <X aria-hidden />
                  </button>
                </div>

                {item.description && <p className="toast__description">{item.description}</p>}

                <div aria-hidden className="toast__progress-track">
                  <div className="toast__progress" />
                </div>
              </div>
            ))}
          </div>,
          document.body
        )}
    </ToastContext.Provider>
  );
}
